import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Filter, X } from "lucide-react";
import { api, CONTENT_TYPE_LABELS } from "@/lib/appApi";
import NewsCard from "@/components/public/NewsCard";
import { Button } from "@/components/ui/button";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function BeritaList() {
  const { units = [], topics = [], unitMap = {} } = useOutletContext() || {};
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const get = (k) => params.get(k) || "";

  const load = useCallback(async () => {
    setLoading(true);
    const query = {};
    ["unit", "topic", "content_type", "program", "q", "month", "year"].forEach((k) => { if (get(k)) query[k] = get(k); });
    query.page = page; query.limit = 12;
    try {
      const r = await api.get("/public/articles", { params: query });
      setData(r.data);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [params]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const years = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

  const activeCount = ["unit", "topic", "content_type", "program", "q", "month", "year"].filter((k) => get(k)).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-extrabold text-dlh-green">Berita DLH</h1>
        <p className="text-gray-600 mt-2">Seluruh berita dan informasi yang telah diterbitkan Dinas Lingkungan Hidup Kabupaten Mimika.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1">
          <div className="bg-dlh-greenPale rounded-lg p-5 sticky top-28" data-testid="filter-panel">
            <div className="flex items-center justify-between mb-4">
              <span className="font-bold text-dlh-green flex items-center gap-2"><Filter className="h-4 w-4" /> Filter</span>
              {activeCount > 0 && <button onClick={clearAll} className="text-xs text-dlh-orange hover:underline flex items-center gap-1"><X className="h-3 w-3" />Reset</button>}
            </div>

            <FilterGroup label="Kata Kunci">
              <input defaultValue={get("q")} onKeyDown={(e) => { if (e.key === "Enter") setFilter("q", e.target.value); }}
                onBlur={(e) => setFilter("q", e.target.value)} placeholder="Cari…" data-testid="filter-keyword"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dlh-orange" />
            </FilterGroup>

            <FilterSelect label="Unit Kerja" value={get("unit")} onChange={(v) => setFilter("unit", v)} testid="filter-unit"
              options={[["", "Semua Unit"], ...units.map((u) => [u.slug, u.short_name])]} />

            <FilterSelect label="Topik" value={get("topic")} onChange={(v) => setFilter("topic", v)} testid="filter-topic"
              options={[["", "Semua Topik"], ...topics.map((t) => [t.slug, t.name])]} />

            <FilterSelect label="Jenis Konten" value={get("content_type")} onChange={(v) => setFilter("content_type", v)} testid="filter-content-type"
              options={[["", "Semua Jenis"], ...Object.entries(CONTENT_TYPE_LABELS)]} />

            <div className="grid grid-cols-2 gap-2">
              <FilterSelect label="Bulan" value={get("month")} onChange={(v) => setFilter("month", v)} testid="filter-month"
                options={[["", "Semua"], ...BULAN.map((b, i) => [String(i + 1), b])]} />
              <FilterSelect label="Tahun" value={get("year")} onChange={(v) => setFilter("year", v)} testid="filter-year"
                options={[["", "Semua"], ...years.map((y) => [String(y), String(y)])]} />
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          {loading ? <p className="text-gray-500">Memuat…</p> : data.items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500" data-testid="no-results">Tidak ada berita yang sesuai filter.</div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">Menampilkan {data.items.length} berita.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="berita-grid">
                {data.items.map((a) => <NewsCard key={a.id} article={a} unitMap={unitMap} />)}
              </div>
              {data.total > 12 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
                  <span className="px-4 py-2 text-sm">Halaman {page}</span>
                  <Button variant="outline" disabled={page * 12 >= data.total} onClick={() => setPage((p) => p + 1)}>Berikutnya</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return <div className="mb-4"><label className="block text-xs font-bold text-dlh-greenMid uppercase mb-1.5">{label}</label>{children}</div>;
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <FilterGroup label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-dlh-orange">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </FilterGroup>
  );
}
